import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import isoLanguages from '../../isoLanguages';
import '../../DashboardHome/settings.css';

// ============================================================
//  Project Settings tab — mirrors the Rush Studio design
//  (rush-studio/project/project-tabs.jsx · ProjectSettingsTab)
// ============================================================

const SET_ICN = {
    caret: (
        <svg className="caret" viewBox="0 0 10 10" fill="none">
            <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    plus: (
        <svg viewBox="0 0 14 14" fill="none" width="14" height="14">
            <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
    x: (
        <svg viewBox="0 0 14 14" fill="none" width="13" height="13">
            <path d="M3.5 3.5l7 7M10.5 3.5l-7 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
    ),
};

// ---------- Primitives (match design class names) ----------
const SetBlock = ({ title, desc, tag, danger, children }) => (
    <div className={`set-block ${danger ? 'set-danger' : ''}`}>
        {(title || desc) && (
            <div className="set-block-head">
                {title && <h3>{title}{tag && <span className="tag">{tag}</span>}</h3>}
                {desc && <p>{desc}</p>}
            </div>
        )}
        {children}
    </div>
);

const SetField = ({ label, desc, miniTag, stack, top, children }) => (
    <div className={`set-field ${stack ? 'stack' : ''} ${top ? 'top' : ''}`}>
        <div className="set-field-label">
            <div className="lbl">{label}{miniTag && <span className="mini-tag">{miniTag}</span>}</div>
            {desc && <div className="desc">{desc}</div>}
        </div>
        <div className="set-field-control">{children}</div>
    </div>
);

const SetSelect = ({ value, options, onChange, minWidth }) => (
    <div className="set-select">
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} style={minWidth ? { minWidth } : undefined}>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {SET_ICN.caret}
    </div>
);

const SetSegmented = ({ value, options, onChange }) => (
    <div className="set-seg" role="tablist">
        {options.map((o) => (
            <button
                key={o.value}
                type="button"
                className={value === o.value ? 'on' : ''}
                role="tab"
                aria-selected={value === o.value}
                onClick={() => onChange(o.value)}
            >
                {o.label}
            </button>
        ))}
    </div>
);

const SetToggle = ({ checked, onChange, disabled }) => (
    <input
        type="checkbox"
        className="set-toggle"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
    />
);

const SetText = ({ value, onChange, placeholder, wide }) => (
    <input
        type="text"
        className={`set-input ${wide ? 'wide' : ''}`}
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange && onChange(e.target.value)}
    />
);

const SetRadioCards = ({ value, options, onChange }) => (
    <div className="set-radio-cards">
        {options.map((o) => (
            <button
                key={o.value}
                type="button"
                className={`set-radio-card ${value === o.value ? 'on' : ''}`}
                onClick={() => onChange(o.value)}
            >
                <div className="rc-top"><span className="rc-dot" />{o.label}</div>
                <div className="rc-desc">{o.desc}</div>
            </button>
        ))}
    </div>
);

const SetLangChips = ({ langs, all, onAdd, onRemove }) => {
    const [open, setOpen] = useState(false);
    const wrapRef = React.useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, [open]);
    const taken = new Set(langs.map((l) => l.code.toLowerCase()));
    const available = all.filter((a) => !taken.has(a.code.toLowerCase()));
    return (
        <div className="set-langs">
            {langs.map((l) => (
                <span key={l.code} className="set-lang-chip">
                    <span className="code">{l.code.toUpperCase()}</span>
                    {l.name}
                    {onRemove && (
                        <button type="button" className="x" aria-label={`Remove ${l.name}`} onClick={() => onRemove(l.code)}>
                            {SET_ICN.x}
                        </button>
                    )}
                </span>
            ))}
            <div style={{ position: 'relative' }} ref={wrapRef}>
                <button type="button" className="set-lang-add" onClick={() => setOpen((o) => !o)}>
                    {SET_ICN.plus} Add language
                </button>
                {open && (
                    <div className="set-lang-pop">
                        {available.length === 0 && <div className="empty">All languages added</div>}
                        {available.map((a) => (
                            <div
                                key={a.code}
                                className="opt"
                                role="button"
                                tabIndex={0}
                                onClick={() => { onAdd(a); setOpen(false); }}
                            >
                                <span className="code">{a.code.toUpperCase()}</span>{a.name}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------- Data ----------
const SOURCE_LANG_OPTIONS = isoLanguages.map((l) => ({ value: l.code, label: l.name }));
const LANG_CATALOG = isoLanguages.map((l) => ({ code: l.code, name: l.name }));

const isoDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toISOString().slice(0, 10);
};

const codeToName = (code) => {
    if (!code) return code;
    const hit = isoLanguages.find((l) => l.code.toLowerCase() === code.toLowerCase());
    return hit ? hit.name : code;
};

const buildInitial = (project, languages) => {
    // Prefer the canonical targets[] list saved on the project; fall back to
    // the languages computed by the parent (overview) or the single primary target.
    let targetSources;
    if (Array.isArray(project.targets) && project.targets.length) {
        targetSources = project.targets.map((code) => ({ code, name: codeToName(code) }));
    } else if (languages && languages.length) {
        targetSources = languages.map((l) => ({ code: l.code, name: codeToName(l.code) }));
    } else if (project.targetLanguage) {
        targetSources = [{ code: project.targetLanguage, name: codeToName(project.targetLanguage) }];
    } else {
        targetSources = [];
    }
    return {
        name: project.projectName || project.name || '',
        folder: project.category || 'Marketing',
        description: project.description || '',
        lifecycleStatus: project.lifecycleStatus || 'live',
        priority: project.priority || 'normal',
        due: isoDate(project.deadline),
        sourceLang: project.sourceLanguage || 'en-us',
        targets: targetSources,
        workflow: project.workflow || 'standard',
        pretranslate: project.pretranslate !== false,
        translationService: project.translationService || 'deepl',
        autoAssign: project.autoAssign !== false,
        inheritQa: project.inheritQa !== false,
        tmScope: project.tmScope || 'workspace',
        glossaryMode: project.glossaryMode || 'warn',
        visibility: project.visibility || 'workspace',
        reviewLink: project.reviewLink !== false,
        reviewerComments: project.reviewerComments !== false,
    };
};

const SettingsTab = ({ project, languages, onSaved, onArchived, onDeleted }) => {
    const token = useSelector((st) => st.auth.token);
    const initial = useMemo(() => buildInitial(project, languages), [project, languages]);
    const [s, setS] = useState(initial);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { setS(initial); setDirty(false); }, [initial]);

    const set = (key, v) => { setS((prev) => ({ ...prev, [key]: v })); setDirty(true); };

    const discard = () => { setS(initial); setDirty(false); setError(null); };

    const save = async () => {
        if (!s.name.trim()) { setError('Project name is required.'); return; }
        setSaving(true);
        setError(null);
        try {
            const primaryTarget = s.targets[0] ? s.targets[0].code : (project.targetLanguage || '');
            await axios.put(`/api/projects/${project._id}`, {
                projectName: s.name.trim(),
                sourceLanguage: s.sourceLang,
                targetLanguage: primaryTarget,
                deadline: s.due ? new Date(s.due).toISOString() : null,
                category: s.folder,
                description: s.description,
                lifecycleStatus: s.lifecycleStatus,
                priority: s.priority,
                workflow: s.workflow,
                pretranslate: s.pretranslate,
                translationService: s.translationService,
                autoAssign: s.autoAssign,
                inheritQa: s.inheritQa,
                tmScope: s.tmScope,
                glossaryMode: s.glossaryMode,
                visibility: s.visibility,
                reviewLink: s.reviewLink,
                reviewerComments: s.reviewerComments,
                targets: s.targets.map((t) => t.code),
            }, { headers: { Authorization: `Bearer ${token}` } });
            setDirty(false);
            if (onSaved) onSaved();
        } catch (err) {
            console.error('Project settings save failed', err);
            setError('Could not save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const archive = async () => {
        if (busy) return;
        if (!window.confirm('Archive this project? It will be hidden from active views and can be restored anytime.')) return;
        setBusy(true);
        try {
            await axios.put(`/api/projects/${project._id}/archive`, { archive: true }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (onArchived) onArchived();
        } catch (err) {
            console.error('Archive failed', err);
            setError('Could not archive the project.');
        } finally {
            setBusy(false);
        }
    };

    const remove = async () => {
        if (busy) return;
        if (!window.confirm(`Delete "${s.name}"? This permanently removes all files, segments, and comments. This cannot be undone.`)) return;
        setBusy(true);
        try {
            await axios.delete(`/api/projects/${project._id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (onDeleted) onDeleted();
        } catch (err) {
            console.error('Delete failed', err);
            setError('Could not delete the project.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="rs-pd-settings set-body" style={{ maxWidth: '100%' }}>
            <SetBlock title="Project details">
                <SetField label="Project name">
                    <SetText value={s.name} onChange={(v) => set('name', v)} />
                </SetField>
                <SetField label="Folder">
                    <SetSelect
                        value={s.folder}
                        onChange={(v) => set('folder', v)}
                        minWidth={170}
                        options={['Marketing', 'Brand', 'Product', 'Support', 'Legal', 'Operations'].map((f) => ({ value: f, label: f }))}
                    />
                </SetField>
                <SetField label="Description" top>
                    <textarea
                        className="set-input wide"
                        style={{ width: 340, minHeight: 80 }}
                        value={s.description}
                        onChange={(e) => set('description', e.target.value)}
                    />
                </SetField>
                <SetField label="Status">
                    <SetSegmented
                        value={s.lifecycleStatus}
                        onChange={(v) => set('lifecycleStatus', v)}
                        options={[
                            { value: 'live', label: 'Live' },
                            { value: 'hold', label: 'On hold' },
                            { value: 'done', label: 'Delivered' },
                        ]}
                    />
                </SetField>
                <SetField label="Priority">
                    <SetSegmented
                        value={s.priority}
                        onChange={(v) => set('priority', v)}
                        options={[
                            { value: 'low', label: 'Low' },
                            { value: 'normal', label: 'Normal' },
                            { value: 'high', label: 'High' },
                            { value: 'urgent', label: 'Urgent' },
                        ]}
                    />
                </SetField>
                <SetField label="Due date">
                    <input
                        type="date"
                        className="set-input"
                        style={{ minWidth: 170 }}
                        value={s.due}
                        onChange={(e) => set('due', e.target.value)}
                    />
                </SetField>
            </SetBlock>

            <SetBlock title="Languages" desc="Source is locked once translation begins. Add targets to expand the project.">
                <SetField label="Source language">
                    <SetSelect
                        value={s.sourceLang}
                        onChange={(v) => set('sourceLang', v)}
                        options={SOURCE_LANG_OPTIONS}
                        minWidth={220}
                    />
                </SetField>
                <SetField label="Target languages" stack top>
                    <SetLangChips
                        langs={s.targets}
                        all={LANG_CATALOG}
                        onAdd={(l) => set('targets', [...s.targets, l])}
                        onRemove={(code) => set('targets', s.targets.filter((t) => t.code !== code))}
                    />
                </SetField>
            </SetBlock>

            <SetBlock title="Workflow & automation">
                <div className="set-field stack top">
                    <div className="set-field-label">
                        <div className="lbl">Workflow</div>
                        <div className="desc">Stages every language passes through before delivery.</div>
                    </div>
                    <div className="set-field-control" style={{ width: '100%', maxWidth: 'none' }}>
                        <SetRadioCards
                            value={s.workflow}
                            onChange={(v) => set('workflow', v)}
                            options={[
                                { value: 'express', label: 'Express', desc: 'MT + a single light review pass.' },
                                { value: 'standard', label: 'Standard', desc: 'AI draft → translate → review → approve.' },
                                { value: 'premium', label: 'Premium', desc: 'Two-pass human review + QA sign-off.' },
                            ]}
                        />
                    </div>
                </div>
                <SetField label="Pre-translate on import" desc="Seed empty segments with machine translation.">
                    <SetToggle checked={s.pretranslate} onChange={(v) => set('pretranslate', v)} />
                </SetField>
                <SetField
                    label="Machine translation engine"
                    desc="Used when pre-translating new files and seeding empty segments."
                >
                    <SetSelect
                        value={s.translationService}
                        onChange={(v) => set('translationService', v)}
                        minWidth={180}
                        options={[
                            { value: 'deepl', label: 'DeepL' },
                            { value: 'google', label: 'Google Cloud' },
                            { value: 'openai', label: 'OpenAI' },
                            { value: 'none', label: 'No engine' },
                        ]}
                    />
                </SetField>
                <SetField label="Auto-assign by language" desc="Route each language to its default linguist automatically.">
                    <SetToggle checked={s.autoAssign} onChange={(v) => set('autoAssign', v)} />
                </SetField>
            </SetBlock>

            <SetBlock title="Quality & memory">
                <SetField label="Inherit workspace QA checks" desc="Use the quality rules configured in workspace Settings.">
                    <SetToggle checked={s.inheritQa} onChange={(v) => set('inheritQa', v)} />
                </SetField>
                <SetField label="Translation memory scope" desc="Which memory this project reads matches from.">
                    <SetSegmented
                        value={s.tmScope}
                        onChange={(v) => set('tmScope', v)}
                        options={[
                            { value: 'project', label: 'This project' },
                            { value: 'workspace', label: 'Workspace' },
                            { value: 'all', label: 'All projects' },
                        ]}
                    />
                </SetField>
                <SetField label="Glossary enforcement" desc="What happens when a target ignores an approved term.">
                    <SetSegmented
                        value={s.glossaryMode}
                        onChange={(v) => set('glossaryMode', v)}
                        options={[
                            { value: 'off', label: 'Off' },
                            { value: 'warn', label: 'Warn' },
                            { value: 'block', label: 'Block' },
                        ]}
                    />
                </SetField>
            </SetBlock>

            <SetBlock title="Access & sharing">
                <div className="set-field stack top">
                    <div className="set-field-label">
                        <div className="lbl">Visibility</div>
                        <div className="desc">Who can open this project.</div>
                    </div>
                    <div className="set-field-control" style={{ width: '100%', maxWidth: 'none' }}>
                        <SetRadioCards
                            value={s.visibility}
                            onChange={(v) => set('visibility', v)}
                            options={[
                                { value: 'private', label: 'Private', desc: 'Only owners and collaborators.' },
                                { value: 'workspace', label: 'Workspace', desc: 'Anyone in your workspace.' },
                                { value: 'public', label: 'Public review', desc: 'Anyone with the review link.' },
                            ]}
                        />
                    </div>
                </div>
                <SetField
                    label="Client review link"
                    miniTag={`rush.studio/r/${(project._id || '').toString().slice(-6) || 'project'}`}
                    desc="Share a read-only review portal with external stakeholders."
                >
                    <SetToggle checked={s.reviewLink} onChange={(v) => set('reviewLink', v)} />
                </SetField>
                <SetField label="Allow reviewer comments" desc="Let people with the review link leave comments on segments.">
                    <SetToggle checked={s.reviewerComments} onChange={(v) => set('reviewerComments', v)} />
                </SetField>
            </SetBlock>

            <SetBlock title="Archive project" danger>
                <SetField
                    label="Archive this project"
                    desc="Hide it from active views. Translation memory is preserved and it can be restored anytime."
                >
                    <button type="button" className="set-btn sm" onClick={archive} disabled={busy}>
                        Archive
                    </button>
                </SetField>
            </SetBlock>

            <SetBlock title="Delete project" danger>
                <SetField
                    label={`Delete ${s.name || 'this project'}`}
                    desc="Permanently removes all files, segments, and comments. This cannot be undone."
                >
                    <button type="button" className="set-btn sm danger" onClick={remove} disabled={busy}>
                        Delete project
                    </button>
                </SetField>
            </SetBlock>

            {error && (
                <div
                    style={{
                        padding: '10px 14px',
                        background: '#fbe1e1',
                        border: '1px solid color-mix(in oklab, #b53e3e 35%, transparent)',
                        color: '#b53e3e',
                        borderRadius: 8,
                        fontSize: 13,
                        marginBottom: 12,
                    }}
                >
                    {error}
                </div>
            )}

            <div className={`set-savebar ${dirty ? '' : 'saved'}`}>
                <div className={`msg ${dirty ? '' : 'saved'}`}>
                    <span className="dot" />
                    {dirty ? 'You have unsaved changes' : 'All changes saved'}
                </div>
                <div className="grow" />
                <button type="button" className="set-btn" onClick={discard} disabled={!dirty || saving}>
                    Discard
                </button>
                <button type="button" className="set-btn primary" onClick={save} disabled={!dirty || saving}>
                    {saving ? 'Saving…' : 'Save changes'}
                </button>
            </div>
        </div>
    );
};

export default SettingsTab;
